import { loadSharingOptions } from "projectDesign/components/projectConfiguration/fields/loadSharingType";
import nextUid from "__common/calculations/nextUid";
import store from "__common/store";

export interface gridSettings {
  rowSpacingMeters: number;
  columnSpacingMeters: number;
  moduleWidthMeters: number;
  moduleLengthMeters: number;
  productId: number;
  metersPerPixel: number;
}


export const addAttachmentsOnCornersAndEdgesForEachArray = (panels: panelInState[], rowSpacingPx: number, columnSpacingPx: number, loadSharingType: string) => {
  const panelMap = new Map();
  const panelsById = new Map(panels.map((panel) => [panel.id, panel]));
  const corners = [];
  const edges = [];

    // Create a map for checking
  panels.forEach(({ x, y, id }) => {
      panelMap.set(`${Number(x.toFixed(4))},${Number(y.toFixed(4))}`, id);
    });

  const panelNeighbours = (panel) => {
      return {
        top: panelMap.get(`${Number(panel.x.toFixed(4))},${Number((panel.y - rowSpacingPx - panel.height).toFixed(4))}`),
        bottom: panelMap.get(`${Number(panel.x.toFixed(4))},${Number((panel.y + rowSpacingPx + panel.height).toFixed(4))}`),
        left: panelMap.get(`${Number((panel.x - columnSpacingPx - panel.width).toFixed(4))},${Number(panel.y.toFixed(4))}`),
        right: panelMap.get(`${Number((panel.x + columnSpacingPx + panel.width).toFixed(4))},${Number(panel.y.toFixed(4))}`),
      };
    };

    // Classify corners and edges
  panels.forEach((panel) => {
      const neighbors = panelNeighbours(panel);

      if ((!neighbors.top && !neighbors.left) || (!neighbors.top && !neighbors.right) ||
            (!neighbors.bottom && !neighbors.left) || (!neighbors.bottom && !neighbors.right)) {
          corners.push(panel.id);
          panel.attached = true;
        } 
      else if (!neighbors.top || !neighbors.bottom || !neighbors.left || !neighbors.right) {
          edges.push(panel.id);
        }
    });

  if (loadSharingType === loadSharingOptions.fmg.value) {
      edges.forEach(edgeId => {
        const edgePanel = panelsById.get(edgeId);
        const edgePanelNeighbours = panelNeighbours(edgePanel);
        if (edgePanelNeighbours?.top && edgePanelNeighbours?.bottom) {
          const topPanel = panelsById.get(edgePanelNeighbours.top);
          const bottomPanel = panelsById.get(edgePanelNeighbours.bottom);

          if (!topPanel.attached && !bottomPanel.attached) {
            edgePanel.attached = true;
          }
          else if (!topPanel.attached && (bottomPanel.attached && corners.includes(bottomPanel.id))){
            edgePanel.attached = true;
          }
        }

        else if (edgePanelNeighbours?.left && edgePanelNeighbours?.right) {
          const leftPanel = panelsById.get(edgePanelNeighbours.left);
          const rightPanel = panelsById.get(edgePanelNeighbours.right);

          if (!leftPanel.attached && !rightPanel.attached) {
            edgePanel.attached = true;
          }
          else if (!leftPanel.attached && (rightPanel.attached && corners.includes(rightPanel.id))){
            edgePanel.attached = true;
          }
        }
        else {
          edgePanel.attached = false;
        }
      });
    }
    

  return panels;
};

export const constructGraph = (panels: panelInState[], rowSpacingPx: number, columnSpacingPx: number) => {
  const leftMap = {};
  const rightMap = {};
  const topMap = {};
  const bottomMap = {};
  const precision = 1;

  const getKey = (x, y) => `${x.toFixed(precision)}-${y.toFixed(precision)}`;

  const getNeighborCoords = p => {
    const leftXCoord = p.x - columnSpacingPx - p.width;
    const leftYCoord = p.y;
    const leftCoord =  getKey(leftXCoord, leftYCoord);

    const rightXCoord = p.x + columnSpacingPx + p.width;
    const rightYCoord = p.y;
    const rightCoord =  getKey(rightXCoord, rightYCoord);

    const topXCoord = p.x;
    const topYCoord = p.y - rowSpacingPx - p.height;
    const topCoord =  getKey(topXCoord, topYCoord);

    const bottomXCoord = p.x;
    const bottomYCoord = p.y + rowSpacingPx + p.height;
    const bottomCoord = getKey(bottomXCoord, bottomYCoord);

    return { leftCoord, rightCoord, topCoord, bottomCoord };
    
  };
  const _panels = [];
  panels.forEach(p=>{
    const pData = { id: p.id, groupId: p.groupId, left: null, right: null, top: null, bottom: null };
    const pKey = getKey(p.x, p.y);
    if (leftMap[pKey]) {
      const p1Data = leftMap[pKey];
      p1Data.left = pData;
      pData.right = p1Data;
    } 
    if (rightMap[pKey]) {
      const p1Data = rightMap[pKey];
      p1Data.right = pData;
      pData.left = p1Data;
    } 
    if (topMap[pKey]) {
      const p1Data = topMap[pKey];
      p1Data.top = pData;
      pData.bottom = p1Data;
    }
    if (bottomMap[pKey]) {
      const p1Data = bottomMap[pKey];
      p1Data.bottom = pData;
      pData.top = p1Data;
    }
    const { leftCoord, rightCoord, topCoord, bottomCoord } = getNeighborCoords(p);  
    leftMap[leftCoord] = pData;
    rightMap[rightCoord] = pData;
    topMap[topCoord] = pData;
    bottomMap[bottomCoord] = pData;
    _panels.push(pData);
  });
  return _panels;
};

export const createArrayGrid = (panels: panelInState[]) => {
  const EMPTY_CELL = 0;
  const {
    settings: {
      rowSpacing, 
      columnSpacing,
    },
    background: {
      metersPerPixel,
    },
  } = store.getState();

  const panelHeight = panels[0].height;
  const panelWidth = panels[0].width;
    
  const moduleLengthMeters = panelHeight * metersPerPixel;
  const moduleWidthMeters = panelWidth * metersPerPixel;
  const xMargin = 0.001 + columnSpacing + 0.05;
  const yMargin = 0.001 + rowSpacing + 0.05;
  const xValues = panels.map((panel) => panel.x * metersPerPixel);
  const yValues = panels.map((panel) => panel.y * metersPerPixel);

  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);

  const dxRaw = (xMax - xMin) / (moduleWidthMeters + columnSpacing);
  const dyRaw = (yMax - yMin) / (moduleLengthMeters + rowSpacing);

  let dx = Number(Math.round(dxRaw)) + 1;

  let dy = Number(Math.round(dyRaw)) + 1;

  const grid = [];

  for (let i = 0; i < dy; i++) {
    grid.push([]);
    for (let j = 0; j < dx; j++) {
        grid[i].push(EMPTY_CELL);
      }
  }
  panels.forEach(panel => {
    const { x, y } = panel;
    const column = Math.abs(
      Number((
          (xMin - x * metersPerPixel) / (moduleWidthMeters + columnSpacing)
      ).toFixed(4)),
      );

    const row = Math.abs(
      Number((
          (yMin - y * metersPerPixel) / (moduleLengthMeters + rowSpacing)
      ).toFixed(4)),
      );
    dx = Math.abs(column - Number(column.toFixed(1)));
    dy = Math.abs(row - Number(row.toFixed(1)));

    if (dx < xMargin && dy < yMargin) {
        const columnIndex = Number(Math.round(column));
        const rowIndex = Number(Math.round(row));
        grid[rowIndex][columnIndex] = panel;
      }
  });
  return grid;
};

const collectConnectedComponent = (panel) => {
  const stack = [panel];
  const component = [];
  const seen = new Set();
  const neighbors = ['left', 'right', 'top', 'bottom', 'sibling'];

  while (stack.length) {
    const current = stack.pop();
    if (!current || seen.has(current.id)) {
      continue;
    }
    seen.add(current.id);
    component.push(current);
    neighbors.forEach((key) => {
      if (current[key] && !seen.has(current[key].id)) {
        stack.push(current[key]);
      }
    });
  }

  return component;
};

const getStableComponentGroupId = (component: Array<{ groupId?: number }>, claimedGroupIds: Set<number>): number => {
  const existingGroupIds = Array.from(new Set(
    component
      .map((panel) => panel.groupId)
      .filter((groupId): groupId is number => typeof groupId === 'number' && groupId > 0),
  ));

  const unclaimedGroupIds = existingGroupIds.filter((groupId) => !claimedGroupIds.has(groupId));
  return unclaimedGroupIds.length ? Math.min(...unclaimedGroupIds) : nextUid();
};

export const getGroupIdForNewComponent = (
  component: Array<{ id: number, groupId: number }>,
  allPanels: Array<{ id: number, groupId: number }>,
  existingGroupIds: Set<number>,
): number => {
  if (existingGroupIds.size) {
    return Math.min(...Array.from(existingGroupIds));
  }

  const proposedGroupId = component[0]?.groupId;
  if (typeof proposedGroupId !== 'number' || proposedGroupId <= 0) {
    return nextUid();
  }

  const componentIds = new Set(component.map((panel) => panel.id));
  const groupIdUsedElsewhere = allPanels.some((panel) => (
    panel.groupId === proposedGroupId && !componentIds.has(panel.id)
  ));

  return groupIdUsedElsewhere ? nextUid() : proposedGroupId;
};

const getClassificationPanelsFromGraph = <T extends { id: number, groupId: number }>(
  panels: T[],
  seedPanelIds: number[],
  graph: Array<{ id: number, groupId?: number }>,
): T[] => {
  const graphById = new Map(graph.map((node) => [node.id, node]));
  const seedIdSet = new Set(seedPanelIds);
  const workerGroupById = new Map<number, number>();

  seedPanelIds.forEach((seedId) => {
    if (workerGroupById.has(seedId)) {
      return;
    }

    const seed = graphById.get(seedId);
    const component = seed ? collectConnectedComponent(seed) : [{ id: seedId, groupId: panels.find((panel) => panel.id === seedId)?.groupId }];
    const hostGroupId = component
      .filter((node) => !seedIdSet.has(node.id))
      .map((node) => node.groupId)
      .find((groupId): groupId is number => typeof groupId === 'number' && groupId > 0);
    const workerGroupId = hostGroupId
      ?? component.find((node) => typeof node.groupId === 'number' && node.groupId > 0)?.groupId
      ?? seedId;

    component.forEach((node) => workerGroupById.set(node.id, workerGroupId));
  });

  return panels
    .filter((panel) => workerGroupById.has(panel.id))
    .map((panel) => (
      panel.groupId === workerGroupById.get(panel.id)
        ? panel
        : { ...panel, groupId: workerGroupById.get(panel.id) as number }
    ));
};

export const getRmdtClassificationPanels = <T extends { id: number, groupId: number, siblingId?: number, x: number, y: number, width: number, height: number }>(
  panels: T[],
  seedPanelIds: number[],
  rowSpacingPx: number,
  columnSpacingPx: number,
): T[] => {
  if (!seedPanelIds.length) {
    return panels;
  }

  return getClassificationPanelsFromGraph(
    panels,
    seedPanelIds,
    constructGraphForRMDT(panels, rowSpacingPx, columnSpacingPx),
  );
};

export const getConnectedClassificationPanels = <T extends { id: number, groupId: number, x: number, y: number, width: number, height: number }>(
  panels: T[],
  seedPanelIds: number[],
  rowSpacingPx: number,
  columnSpacingPx: number,
): T[] => {
  if (!seedPanelIds.length) {
    return panels;
  }

  return getClassificationPanelsFromGraph(
    panels,
    seedPanelIds,
    constructGraph(panels, rowSpacingPx, columnSpacingPx),
  );
};

export const splitArraysIntoSubarrays = (graph) => {
  const visited = {};
  const claimedGroupIds = new Set<number>();
  graph.forEach((panel) => {
    visited[panel.id] = 0;
  });
  graph.forEach((panel) => {
    if (!visited[panel.id]) {
      const component = collectConnectedComponent(panel);
      const groupId = getStableComponentGroupId(component, claimedGroupIds);
      claimedGroupIds.add(groupId);
      component.forEach((componentPanel) => {
        visited[componentPanel.id] = groupId;
      });
    }
  });
  return visited;
};

export const constructGraphForRMDT = (panels, rowSpacingPx, columnSpacingPx) => {
  const leftMap = {};
  const rightMap = {};
  const topMap = {};
  const bottomMap = {};
  const precision = 1;

  const getKey = (x, y) => `${x.toFixed(precision)}-${y.toFixed(precision)}`;

  const getNeighborCoords = (p) => {
    const leftCoord = getKey(p.x - columnSpacingPx - p.width, p.y);
    const rightCoord = getKey(p.x + columnSpacingPx + p.width, p.y);
    const topCoord = getKey(p.x, p.y - rowSpacingPx - p.height);
    const bottomCoord = getKey(p.x, p.y + rowSpacingPx + p.height);
    return { leftCoord, rightCoord, topCoord, bottomCoord };
  };

  const _panels = [];
  const siblingMap = new Map();
  panels.forEach((p) => {
    const pData = { id: p.id, groupId: p.groupId, siblingId: p.siblingId, left: null, right: null, top: null, bottom: null };
    const pKey = getKey(p.x, p.y);
    
    if (p.siblingId !== undefined && p.siblingId !== null && p.siblingId > 0) {
      if (!siblingMap.has(p.siblingId)) {
        siblingMap.set(p.siblingId, []);
      }
      siblingMap.get(p.siblingId).push(pData);
    }

    if (leftMap[pKey]) {
      leftMap[pKey].left = pData;
      pData.right = leftMap[pKey];
    }
    if (rightMap[pKey]) {
      rightMap[pKey].right = pData;
      pData.left = rightMap[pKey];
    }
    if (topMap[pKey]) {
      topMap[pKey].top = pData;
      pData.bottom = topMap[pKey];
    }
    if (bottomMap[pKey]) {
      bottomMap[pKey].bottom = pData;
      pData.top = bottomMap[pKey];
    }
    
    const { leftCoord, rightCoord, topCoord, bottomCoord } = getNeighborCoords(p);
    leftMap[leftCoord] = pData;
    rightMap[rightCoord] = pData;
    topMap[topCoord] = pData;
    bottomMap[bottomCoord] = pData;
    _panels.push(pData);
  });

  siblingMap.forEach((siblings) => {
    if (siblings.length === 2) {
      siblings[0].sibling = siblings[1];
      siblings[1].sibling = siblings[0];
    }
  });

  return _panels;
};
